import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Heading, Spinner } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntercom } from "react-use-intercom";
import type {
  SelectionCriterionDto,
  SourcingEventV2Dto,
  SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import {
  QualificationRequirementDrawer,
  type QualificationFormValues,
} from "../components/QualificationRequirementDrawer";
import { ExclusionGroundsSection } from "../components/ExclusionGroundsSection";
import { QualificationRequirementList } from "../components/QualificationRequirementList";
import { useEditor } from "../../../../../common/editing/save-coordinator";

// t('Delete requirement') t('Are you sure you want to delete this requirement?') t('Delete')

type DrawerMode =
  | { kind: "add" }
  | { kind: "edit"; index: number; initialValues: QualificationFormValues };

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  isActive: boolean;
}

export const QualificationStep = ({ eventId, previousStep, nextStep, nextStepLabel, isActive }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const [drawerState, setDrawerState] = useState<DrawerMode | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);
  const confirmationModal = useConfirmationModal();
  const { update: updateIntercom } = useIntercom();

  const items = useMemo(() => state.eventData.selectionCriteria ?? [], [state.eventData.selectionCriteria]);
  const isSavingCriteria = (() => {
    const s = fieldStatus("eventData.selectionCriteria");
    return s === "saving" || s === "pending";
  })();

  useEffect(() => {
    if (!isActive) setDrawerState(null);
  }, [isActive]);

  const isDrawerOpen = drawerState !== null;
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isDrawerOpen });
  }, [isDrawerOpen, updateIntercom]);

  const handleSubmit = useCallback(
    async (values: SelectionCriterionDto) => {
      const newList =
        drawerState?.kind === "edit"
          ? items.map((item, i) => (i === drawerState.index ? values : item))
          : [...items, values];
      updateField({ eventData: { selectionCriteria: newList } });
      setDrawerState(null);
    },
    [items, updateField, drawerState],
  );

  const handleEdit = useCallback(
    (index: number) => {
      const item = items[index];
      setDrawerKey((k) => k + 1);
      setDrawerState({
        kind: "edit",
        index,
        initialValues: {
          type: item.type,
          name: item.name ?? "",
          description: item.description,
          requiresEvidence: item.requiresEvidence,
        },
      });
    },
    [items],
  );

  const handleDelete = useCallback(
    (index: number) => {
      confirmationModal.show({
        title: t("Delete requirement"),
        description: t("Are you sure you want to delete this requirement?"),
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: () => {
          const newList = items.filter((_, i) => i !== index);
          updateField({ eventData: { selectionCriteria: newList } });
        },
      });
    },
    [items, updateField, confirmationModal, t],
  );

  return (
    <Box pb="8" pt="10">
      <Flex justify="space-between" align="center" mb="6">
        <Heading as="h2" size="md">
          {t("Qualification requirements")} {isSavingCriteria && <Spinner size="xs" />}
        </Heading>
        <Button
          colorScheme="teal"
          leftIcon={<AddIcon />}
          onClick={() => {
            setDrawerKey((k) => k + 1);
            setDrawerState({ kind: "add" });
          }}
        >
          {t("Add requirement")}
        </Button>
      </Flex>

      <Box mb="8">
        <QualificationRequirementList
          items={items}
          onAdd={() => {
            setDrawerKey((k) => k + 1);
            setDrawerState({ kind: "add" });
          }}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Box>

      <ExclusionGroundsSection eventId={eventId} />

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />

      <QualificationRequirementDrawer
        key={drawerKey}
        isOpen={drawerState !== null}
        isSaving={isSavingCriteria}
        mode={drawerState?.kind ?? "add"}
        defaultValues={drawerState?.kind === "edit" ? drawerState.initialValues : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawerState(null)}
      />

      {confirmationModal.modal}
    </Box>
  );
};
